import React from "react";
import { View, ScrollView, TouchableOpacity, Linking } from "react-native";
import styled from "styled-components/native";
import { useLinkTo } from "@react-navigation/native";
import VDBIcon, { IconsVariant } from "@components/icons/VDBIcon";
import { VDBIcons } from "@utils/VDBIcons";
import { SpaceType } from "@components/atoms/Spacer/types";
import { Button, Spacer, VDBText } from "@components/atoms";
import { ButtonCategory, ButtonType } from "@components/atoms/Button/types";
import useScreenOptionsHooks from "../../../../hooks/useScreenOptionsHooks";
import Routes from "@navigations/Routes";
import { navigate } from "@navigations/utils/RootNavigation";
import LineViewContainer from "@scenes/requests/utils/LineView";
import { RedirectFunc } from "../utils";
import { ProductCategory } from "@utils/constants";
import { RequestTypes } from "@scenes/requests/utils/utils";
import ShippingInformation from "../ShippingInformation";
import { useOrgSettingsSlice } from "@redux/slices/orgSettingsSlice";
import { useQueryClient } from "@tanstack/react-query";
import {
  DynamicSuccessPopUpProps,
  ProcessedSection,
  ProcessedField,
} from "./DynamicSuccessPopUp.types";
import {
  processConfigIntoSections,
  getSectionLabel,
  formatFieldValue,
  extractContactSection,
  extractShippingInformationSection,
  extractWireTransferSection,
} from "./DynamicSuccessPopUp.utils";

const DynamicSuccessPopUp: React.FC<DynamicSuccessPopUpProps> = ({
  successData,
  config,
  deepLink,
  productType,
  priceMode,
  onClose,
  onPrimaryAction,
  onSecondaryAction,
}) => {
  const { theme, typography, t } = useScreenOptionsHooks();
  const linkTo = useLinkTo();
  const queryClient = useQueryClient();
  const { shoppingCartOfflinePaymentInstructions } = useOrgSettingsSlice();

  const sections = processConfigIntoSections(
    config,
    successData?.order || successData
  );
  const contactSection = extractContactSection(
    config,
    successData?.order || successData
  );
  const shippingInfoSection = extractShippingInformationSection(
    config,
    successData?.order || successData
  );
  const wireTransferSection = extractWireTransferSection(
    config,
    shoppingCartOfflinePaymentInstructions
  );

  const allSections = [
    ...sections.map((section) => ({ ...section, type: "regular" })),
    ...(shippingInfoSection
      ? [
          {
            ...shippingInfoSection,
            type: "shipping",
            key: "shipping_information",
          },
        ]
      : []),
    ...(wireTransferSection
      ? [
          {
            ...wireTransferSection,
            type: "wire_transfer",
            key: "wire_transfer",
          },
        ]
      : []),
    ...(contactSection ? [{ ...contactSection, type: "contact" }] : []),
  ].sort((a, b) => a.order - b.order);

  const navigateToOrder = () => {
    const navigateParams = {
      selectedTab: "sent",
      initial: false,
      screen: Routes.RequestDetailScreen,
      params: {
        id: successData?.order?.id || successData?.id,
        selectedTab: "sent",
        requestName: RequestTypes.Order,
        productType: productType ?? ProductCategory.JEWELRY,
        price_mode: priceMode,
      },
    };
    queryClient.invalidateQueries({ queryKey: ["stock_item_request"] });
    queryClient.invalidateQueries({ queryKey: ["GET_UNREAD_REQUEST"] });
    if (theme.isMobile) {
      navigate(Routes.GlobalNavigator, navigateParams);
    } else {
      navigate(Routes.AppNavigator, {
        screen: Routes.RequestHistoryNavigator,
        params: navigateParams,
      });
    }
  };

  const handlePrimaryAction = () => {
    onClose?.();
    if (onPrimaryAction) {
      onPrimaryAction();
    } else if (deepLink) {
      RedirectFunc(deepLink, linkTo);
    } else {
      navigate(Routes.ExploreNavigator, {
        screen: Routes.Explore,
        initial: false,
      });
    }
  };

  const handleSecondaryAction = () => {
    onClose?.();
    if (onSecondaryAction) {
      onSecondaryAction();
    } else {
      navigateToOrder();
    }
  };

  const renderField = (field: ProcessedField) => {
    const isEmail = field.key.includes("email");
    const isPhone = field.key.includes("phone") || field.key.includes("mobile");
    const isWebsite =
      field.key.includes("website") || field.key.includes("url");
    const isClickable = isEmail || isPhone || isWebsite;

    const handlePress = () => {
      if (isEmail) {
        handleEmailPress(field.value);
      } else if (isPhone) {
        handlePhonePress(field.value);
      } else if (isWebsite) {
        handleWebsitePress(field.value);
      }
    };

    return (
      <InfoRow key={field.key}>
        <Label>
          <VDBText color={theme["core-06"]} type={typography["Minor-R"]}>
            {t(field.label)}:
          </VDBText>
        </Label>
        <Spacer size={SpaceType.ExtraSmall} />
        <Value>
          {isClickable ? (
            <TouchableOpacity onPress={handlePress}>
              <VDBText
                color={theme["link-static"]}
                type={typography["Minor-M"]}
              >
                {formatFieldValue(field.key, field.value)}
              </VDBText>
            </TouchableOpacity>
          ) : (
            <VDBText color={theme["core-05"]} type={typography["Minor-M"]}>
              {formatFieldValue(field.key, field.value)}
            </VDBText>
          )}
        </Value>
      </InfoRow>
    );
  };

  const renderSection = (
    section: ProcessedSection,
    isContact: boolean = false
  ) => (
    <SectionContainer key={section.key}>
      {section.label && (
        <>
          <TextContainer>
            <VDBText color={theme["core-05"]} type={typography["H3-M"]}>
              {getSectionLabel(section.label, section.key, t)}
            </VDBText>
            {section.secondaryLabel && (
              <>
                <Spacer size={SpaceType.Basic} />
                <VDBText color={theme["core-05"]} type={typography["H3-M"]}>
                  {getSectionLabel(section.secondaryLabel, section.key, t)}
                </VDBText>
              </>
            )}
          </TextContainer>
          <LineViewContainer background="core-03" />
        </>
      )}
      <InfoContainer isContact={isContact}>
        {section.fields.map((field) => renderField(field))}
      </InfoContainer>
    </SectionContainer>
  );

  const handleEmailPress = async (email: string) => {
    try {
      await Linking.openURL(`mailto:${email}`);
    } catch (error) {
      console.error("Failed to open email client:", error);
    }
  };

  const handlePhonePress = async (phone: string) => {
    try {
      const cleanedPhone = phone.replace(/[^\d+]/g, "");
      await Linking.openURL(`tel:${cleanedPhone}`);
    } catch (error) {
      console.error("Failed to open phone dialer:", error);
    }
  };

  const handleWebsitePress = async (url: string) => {
    try {
      let formattedUrl = url;
      if (!url.match(/^https?:\/\//)) {
        formattedUrl = `https://${url}`;
      }
      const supported = await Linking.canOpenURL(formattedUrl);
      if (supported) {
        await Linking.openURL(formattedUrl);
      }
    } catch (error) {
      console.error("Failed to open website:", error);
    }
  };

  const buyerEmail =
    successData?.order?.buyer?.email || successData?.buyer?.email;

  return (
    <ScrollView showsVerticalScrollIndicator={false}>
      <ParentContainer>
        {/* Success Message Header */}
        <MessageIconContainer>
          <VDBIcon
            color={theme["success-01"]}
            size={40}
            variant={IconsVariant.VDBCustom}
            name={VDBIcons.CHECKMARK}
          />
          <Spacer size={SpaceType.Basic} />
          <VDBText color={theme["success-01"]} type={typography["H2-R"]}>
            {t("orderCart.successPopUp.message.title.text")}
          </VDBText>
          <Spacer size={SpaceType.ExtraSmall} />
          <VDBText
            color={theme["core-06"]}
            type={typography["Body-R"]}
            textAlign={"center"}
          >
            {t("orderCart.successPopUp.message.description.text").replace(
              "{{x}}",
              ""
            )}
            {buyerEmail && (
              <VDBText color={theme["core-05"]} type={typography["Body-R"]}>
                {buyerEmail}
              </VDBText>
            )}
          </VDBText>
          <Spacer size={SpaceType.Big} />
        </MessageIconContainer>

        <CenterContainer>
          {allSections.map((section) => {
            if (section.type === "regular") {
              return (
                <React.Fragment key={section.key}>
                  {renderSection(section)}
                  <Spacer size={SpaceType.Basic} />
                </React.Fragment>
              );
            } else if (section.type === "shipping") {
              return (
                <React.Fragment key={section.key}>
                  <ShippingInformationContainer>
                    <ShippingInformation
                      title={section.title}
                      description={section.description}
                      descriptionHeight={section.descriptionHeight}
                    />
                  </ShippingInformationContainer>
                  <Spacer size={SpaceType.Basic} />
                </React.Fragment>
              );
            } else if (section.type === "wire_transfer") {
              return (
                <React.Fragment key={section.key}>
                  <ShippingInformationContainer>
                    <ShippingInformation
                      title={t(section.title)}
                      description={section.description}
                      descriptionHeight={section.descriptionHeight}
                    />
                  </ShippingInformationContainer>
                  <Spacer size={SpaceType.Basic} />
                </React.Fragment>
              );
            } else if (section.type === "contact") {
              return (
                <React.Fragment key={section.key}>
                  {renderSection(section, true)}
                  <Spacer size={SpaceType.Basic} />
                </React.Fragment>
              );
            }
            return null;
          })}
        </CenterContainer>

        <Spacer size={SpaceType.ExtraLarge} />

        <ButtonContainer>
          <Button
            category={ButtonCategory.default}
            type={ButtonType.primary}
            title={t("orderCart.successPopUp.button.primary.text")}
            onPress={handlePrimaryAction}
            maxWidth={theme.isLargeScreen ? 343 : 608}
          />
          <Spacer size={SpaceType.Double} />
          <Button
            category={ButtonCategory.default}
            type={ButtonType.secondary}
            title={t("orderCart.successPopUp.button.secondary.text")}
            onPress={handleSecondaryAction}
            maxWidth={theme.isLargeScreen ? 343 : 608}
          />
        </ButtonContainer>
      </ParentContainer>
    </ScrollView>
  );
};

export default DynamicSuccessPopUp;

const ParentContainer = styled(View)`
  padding-horizontal: ${SpaceType.Double}px;
  padding-top: ${SpaceType.Double}px;
  padding-bottom: ${SpaceType.ExtraLarge}px;
  background-color: ${({ theme }) => theme["core-02"]};
`;

const MessageIconContainer = styled(View)`
  flex-direction: column;
  justify-content: center;
  align-items: center;
  ${({ theme }) => theme.isTablet && `max-width: 608px;`}
  width: 100%;
  align-self: center;
`;

const CenterContainer = styled(View)`
  justify-content: center;
  align-items: center;
  width: 100%;
`;

const SectionContainer = styled(View)`
  background-color: ${({ theme }) => theme["core-01"]};
  border-radius: ${SpaceType.Basic}px;
  ${({ theme }) => theme.isTablet && `max-width: 608px;`}
  width: 100%;
  margin-bottom: ${SpaceType.Basic}px;
`;

const InfoContainer = styled(View)<{ isContact?: boolean }>`
  padding: ${SpaceType.Basic}px;
  gap: ${SpaceType.Basic}px;
  ${({ isContact, theme }) =>
    isContact &&
    `
    background-color: ${theme["core-01"]};
  `}
`;

const TextContainer = styled(View)`
  padding-horizontal: ${SpaceType.Basic}px;
  padding-vertical: ${SpaceType.Extra}px;
`;

const InfoRow = styled(View)`
  flex-direction: row;
`;

const Label = styled(View)`
  min-width: 112px;
  max-width: 140px;
`;

const Value = styled(View)`
  flex: 1;
`;

const ButtonContainer = styled(View)`
  justify-content: center;
  align-items: center;
`;

const ShippingInformationContainer = styled(View)`
  background-color: ${({ theme }) => theme["core-01"]};
  border-radius: ${SpaceType.Basic}px;
  ${({ theme }) => theme.isTablet && `max-width: 608px;`}
  width: 100%;
  padding: ${SpaceType.Basic}px;
`;
